import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  createPaymentRequest,
  getPayments,
  getPaymentsByUser,
  getUserActiveSubscription,
  removePayment,
  updatePaymentRequest,
} from "../../../services/api/payments/paymentsApi";

const rejectApiResponse = (responseData, fallbackMessage) => {
  if (responseData?.success === false) {
    return responseData.message ?? fallbackMessage;
  }
  return null;
};

const PAYMENT_CREATE_ERROR_FALLBACK = {
  en: "Failed to create payment",
  ar: "فشل في إنشاء الدفعة",
};

const PAYMENT_UPDATE_ERROR_FALLBACK = {
  en: "Failed to update payment",
  ar: "فشل في تحديث الدفعة",
};

export const fetchPaymentsByUser = createAsyncThunk(
  "payments/fetchPaymentsByUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await getPaymentsByUser(userId);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch payments");
    }
  },
);

export const fetchPayments = createAsyncThunk(
  "payments/fetchPayments",
  async (dateRange = {}, { rejectWithValue }) => {
    try {
      const response = await getPayments(dateRange);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch payments");
    }
  },
);

export const deletePayment = createAsyncThunk(
  "payments/deletePayment",
  async (paymentId, { rejectWithValue }) => {
    try {
      await removePayment(paymentId);
      return paymentId;
    } catch (error) {
      return rejectWithValue("Failed to delete payment");
    }
  },
);

export const createPayment = createAsyncThunk(
  "payments/createPayment",
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await createPaymentRequest(paymentData);

      const apiError = rejectApiResponse(
        response.data,
        PAYMENT_CREATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        payment: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, PAYMENT_CREATE_ERROR_FALLBACK),
      );
    }
  },
);

export const updatePayment = createAsyncThunk(
  "payments/updatePayment",
  async ({ paymentId, paymentData }, { rejectWithValue }) => {
    try {
      const response = await updatePaymentRequest(paymentId, paymentData);

      const apiError = rejectApiResponse(
        response.data,
        PAYMENT_UPDATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        payment: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, PAYMENT_UPDATE_ERROR_FALLBACK),
      );
    }
  },
);

export const fetchUserSubscription = createAsyncThunk(
  "payments/fetchUserSubscription",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await getUserActiveSubscription(userId);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch user subscription");
    }
  },
);

const paymentsSlice = createSlice({
  name: "payments",
  initialState: {
    paymentData: [],
    loading: false,
    error: null,
    userSubscription: null,
    userSubscriptionLoading: false,
    userSubscriptionError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentsByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentsByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentData = action.payload;
      })
      .addCase(fetchPaymentsByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentData = action.payload;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deletePayment.fulfilled, (state, action) => {
        state.paymentData = state.paymentData.filter(
          (payment) => payment.id !== action.payload,
        );
        const lang =
          window.localStorage.getItem("language") === "ar" ? "ar" : "en";
        toast.success(
          lang === "ar"
            ? "تم حذف الدفعة بنجاح!"
            : "Payment deleted successfully!",
        );
      })
      .addCase(deletePayment.rejected, (state, action) => {
        state.error = action.payload;
        const lang =
          window.localStorage.getItem("language") === "ar" ? "ar" : "en";
        let msg = action.payload;
        if (lang === "ar") {
          msg =
            typeof msg === "string" && msg.includes("Failed")
              ? "فشل في حذف الدفعة"
              : msg;
        }
        toast.error(
          msg ||
            (lang === "ar" ? "فشل في حذف الدفعة" : "Failed to delete payment"),
        );
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        if (action.payload?.payment) {
          state.paymentData.push(action.payload.payment);
        }
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updatePayment.fulfilled, (state, action) => {
        const payment = action.payload?.payment;
        if (!payment) return;

        const index = state.paymentData.findIndex(
          (item) => item.id === payment.id,
        );
        if (index !== -1) {
          state.paymentData[index] = payment;
        }
      })
      .addCase(updatePayment.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchUserSubscription.pending, (state) => {
        state.userSubscriptionLoading = true;
        state.userSubscriptionError = null;
      })
      .addCase(fetchUserSubscription.fulfilled, (state, action) => {
        state.userSubscriptionLoading = false;
        state.userSubscription = action.payload;
      })
      .addCase(fetchUserSubscription.rejected, (state, action) => {
        state.userSubscriptionLoading = false;
        state.userSubscriptionError = action.payload;
      });
  },
});

export default paymentsSlice.reducer;
